// csv.utils.js
// Pure CSV parsing — no React, no side-effects.

function splitLine(line) {
  const result = [];
  let cur = "", inQ = false;
  for (const ch of line) {
    if (ch === '"') { inQ = !inQ; continue; }
    if (ch === "," && !inQ) { result.push(cur.trim()); cur = ""; continue; }
    cur += ch;
  }
  result.push(cur.trim());
  return result;
}

/**
 * Parse CSV text into recipient rows.
 * Expected columns: Name, Email (or Phone Number), Amount
 *
 * @returns {{
 *   rows: Array<{name, contact, contactType, amount, needsAmount}>,
 *   errors: string[]
 * }}
 */
export function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2)
    return { rows: [], errors: ["CSV needs a header row and at least one data row."] };

  const rawHeaders = splitLine(lines[0]);
  const headers    = rawHeaders.map((h) => h.toLowerCase().replace(/['"]/g, "").trim());
  const col        = (kws) => headers.findIndex((h) => kws.some((k) => h.includes(k)));

  const nameIdx   = col(["name"]);
  const emailIdx  = col(["email"]);
  const phoneIdx  = col(["phone"]);
  const amountIdx = col(["amount", "pay", "price", "$"]);

  const errs = [];
  if (nameIdx   === -1) errs.push('Missing "name" column.');
  if (emailIdx  === -1 && phoneIdx === -1) errs.push('Need "email" or "phone" column.');
  if (amountIdx === -1) errs.push('Missing "amount" column.');
  if (errs.length) return { rows: [], errors: errs };

  const rows      = [];
  const rowErrors = [];

  lines.slice(1).forEach((line, i) => {
    if (!line.trim()) return;
    const cols  = splitLine(line);
    const clean = (idx) => idx !== -1 ? (cols[idx] || "").replace(/^"|"$/g, "").trim() : "";

    const name   = clean(nameIdx);
    const email  = clean(emailIdx);
    const phone  = clean(phoneIdx);
    const rawAmt = clean(amountIdx);
    const amount = rawAmt ? parseFloat(rawAmt.replace(/[$,\s]/g, "")) : NaN;

    if (!name) { rowErrors.push(`Row ${i + 2}: missing name.`); return; }

    const validEmail = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const validPhone = phone && /^\+?[0-9][0-9()\-\s]{6,}$/.test(phone);

    if (!validEmail && !validPhone) {
      rowErrors.push(`Row ${i + 2}: no valid email or phone for "${name}".`);
      return;
    }

    rows.push({
      name,
      contact:     validEmail ? email : phone,
      contactType: validEmail ? "email" : "phone",
      amount:      !isNaN(amount) && amount > 0 ? amount.toString() : "",
      needsAmount: isNaN(amount) || amount <= 0,
    });
  });

  return { rows, errors: rowErrors };
}

/** Generate a downloadable CSV template blob URL */
export function csvTemplateUrl() {
  const csv  = "Name,Email,Phone Number,Amount\nJane Smith,jane@example.com,,150\nJohn Doe,,+13105550101,200\n";
  const blob = new Blob([csv], { type: "text/csv" });
  return URL.createObjectURL(blob);
}